/**
 * 006 · T036 · `InstallCard` (research R-22; contracts/staff-ui.md §3): card discreta bajo
 * "Últimas marcaciones" en la home (montaje en T037, dueño de `home-screen.tsx`), solo en Android
 * no instalado y no pospuesta. En iOS no hay card: la instalación se exige durante la activación
 * (`/instalar`, R-1).
 *
 * Guía animada (brief de instalación §6): con `beforeinstallprompt`, botón nativo "Instalar"
 * (`prompt()`) y "¿No funciona? Hazlo desde el menú del navegador", que abre la animación de
 * Android; sin el evento, "Ver cómo instalar" abre la animación directamente. La animación vive en
 * la hoja de `InstallHost` y se carga en diferido: Inicio no la paga hasta que se pide.
 */
import { useState } from 'react';

import { Button } from '../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../components/ui/card';
import { detectPlatform, isStandalone } from '../../device/platform';
import { t } from '../../i18n';

import { dismissInstallCard, isInstallCardSnoozed } from './install-store';
import { useInstallPrompt } from './use-install-prompt';
import { APP_NAME, openInstallGuide } from './variants';

export function InstallCard() {
  const { canPrompt, installed, prompt } = useInstallPrompt();
  // Solo fuerza el ocultamiento inmediato tras tocar "Ahora no" en este montaje: el plazo de
  // `INSTALL_CARD_SNOOZE_DAYS` que decide si vuelve a aparecer se lee de `localStorage` en cada
  // apertura de la app (próximo montaje), no aquí.
  const [dismissedNow, setDismissedNow] = useState(false);
  const [accepted, setAccepted] = useState(false);

  if (detectPlatform() !== 'android' || isStandalone() || dismissedNow || isInstallCardSnoozed()) {
    return null;
  }

  function handleDismiss(): void {
    dismissInstallCard();
    setDismissedNow(true);
  }

  async function handleInstall(): Promise<void> {
    if ((await prompt()) === 'accepted') {
      setAccepted(true);
    }
  }

  if (installed || accepted) {
    return (
      <Card data-testid="install-card">
        <p
          data-testid="install-card-done"
          role="status"
          className="text-fm-base text-fm-success-text"
        >
          {t('install.installed', { app: APP_NAME })}
        </p>
      </Card>
    );
  }

  return (
    <Card data-testid="install-card">
      <CardTitle>{t('install.title')}</CardTitle>
      <CardDescription>{t('install.lead')}</CardDescription>
      <div className="mt-fm-3 flex flex-col gap-fm-2">
        {canPrompt ? (
          <>
            <Button
              data-testid="install-card-install"
              variant="secondary"
              fullWidth={false}
              onClick={() => void handleInstall()}
            >
              {t('install.action')}
            </Button>
            <button
              type="button"
              data-testid="install-card-manual-help"
              onClick={() => openInstallGuide({ variant: 'android', manual: true })}
              className="inline-flex min-h-11 items-center self-start text-left font-ui text-fm-sm font-semibold text-fm-primary-compact underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus"
            >
              {t('install.manualHelp')}
            </button>
          </>
        ) : (
          <Button
            data-testid="install-card-manual"
            variant="secondary"
            fullWidth={false}
            onClick={() => openInstallGuide({ variant: 'android' })}
          >
            {t('install.manual')}
          </Button>
        )}
        <Button
          data-testid="install-card-dismiss"
          variant="ghost"
          fullWidth={false}
          onClick={handleDismiss}
        >
          {t('install.dismiss')}
        </Button>
      </div>
    </Card>
  );
}
